import { observable, action } from "mobx";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import { reconnectLiSequenceLoadingPopupState } from "./ReconnectLiSequenceLoadingPopupState";
import { confirmationPopupState } from "Components/common/confirmationPopup";

class ReconnectLiSequencePopupState {
  defaultPopupValue = {
    liAccountId: "",
    firstName: "",
    lastName: "",
    profilePictureUrl: "",
    sequences: []
  };

  @observable showPopup = false;
  @observable isReconnecting = false;
  @observable popupValues = { ...this.defaultPopupValue };
  @observable isFetching = false;

  @action
  setShowPopup = (val = false) => {
    this.showPopup = val;
    if (val) {
      document.getElementsByTagName("body")[0].classList.add("scrollHidden");
    } else {
      this.popupValues = { ...this.defaultPopupValue };
      document.getElementsByTagName("body")[0].classList.remove("scrollHidden");
    }
  };

  @action
  setPopupValues = (val = {}) => {
    this.popupValues = { ...val };
  };

  @action
  setSequences = (val = []) => {
    this.popupValues.sequences = [...val];
  };

  // Fetches the sequences this LI account was paused on (due to revoke) and, only if there
  // are any, shows the picker popup. Call this right after a reconnect completes - no-ops
  // silently if there's nothing to resume, so it's safe to call on every reconnect.
  @action
  checkAndShowPopup = async (liAccountId = "", account = {}) => {
    if (!liAccountId) return;
    try {
      this.isFetching = true;
      const config = {
        url: URLS.getCampaignSequencesByLiAccount + "/" + liAccountId,
        method: "GET"
      };
      let res = await makeApi(config);

      if (res?.data?.length > 0) {
        this.setPopupValues({
          liAccountId,
          firstName: account?.firstName || "",
          lastName: account?.lastName || "",
          profilePictureUrl: account?.profilePictureUrl || "",
          sequences: res.data
        });
        this.setShowPopup(true);
      }
    } catch (err) {
      console.error("Error while fetching sequences paused for the li account", err);
    } finally {
      this.isFetching = false;
    }
  };

  @action
  reconnectSequences = async (sequenceIds = []) => {
    try {
      this.isReconnecting = true;
      const config = {
        url: URLS.reconnectResumeLiSeq,
        method: "POST",
        data: {
          liAccountId: this.popupValues.liAccountId || "",
          sequenceIds: sequenceIds
        }
      };
      const res = await makeApi(config);
      const { background = false } = res?.data || {};

      this.setShowPopup(false);
      this.isReconnecting = false;

      if (background) {
        confirmationPopupState.setPopupValues({
          content: `Reconnecting the LinkedIn account to selected sequences…<br>This may take a few minutes.`,
          actionBtnText: "Close",
          needCancelBtn: false,
          callback: () => {
            confirmationPopupState.setShowPopup(false);
          }
        });
        confirmationPopupState.setShowPopup(true);
      } else {
        reconnectLiSequenceLoadingPopupState.setData(res?.data);
        reconnectLiSequenceLoadingPopupState.setShowPopup(true);
      }
    } catch (err) {
      console.error("Error while reconnecting the sequences", err);
      this.isReconnecting = false;
      toasterState.setToastMsg("Error while reconnecting the sequences", "fail");
    } finally {
      this.resetPopupData();
    }
  };

  @action
  resetPopupData = () => {
    this.setPopupValues({ ...this.defaultPopupValue });
  };
}

const reconnectLiSequencePopupState = new ReconnectLiSequencePopupState();

export { reconnectLiSequencePopupState };
export default reconnectLiSequencePopupState;
